import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useEffect, useState } from "react";
import { Box, Checkbox } from "@mui/material";
import "./FEUMRVEnergyMonitoring.scss"
import { useMemo } from "react";
import { convertLocaltoUTCFormat } from "../../../../../../services/DateFormatUtility";
import { callApi } from "../../../../../../services/MonitoringPlanService";
import { AbsDropDown } from "abs-ui-components";
import { MDBCol, MDBRow } from "mdbreact";
import { useSelector } from "react-redux";
import { FEU_CHILD_API_ROUTES } from "../../FEUChildApiRoutes";
import { ENERGYMONITORINGFILTERDROPDOWN } from "../../FEUChildConstant";
import { roundOff } from "../../../../../../services/utilities";

const safeDisplay = (value, decimals = 3) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    if (Number.isNaN(num)) return "";
    return roundOff(num, decimals, decimals);
};

const FEUMRVEnergyMonitoring = () => {
    const [data, setData] = useState([]);
    const [hoveredGroup, setHoveredGroup] = useState(null);
    const [selectedValue, setSelectedValue] = useState("all");
    const [totalRow, setTotalRow] = useState(null);
    const [loading, setLoading] = useState(false);
    const reportDetails = useSelector(
        (state) => state.reports.selectedReportDetails
    );

    // Treat numeric strings like "0", "0.00", "0.000" as zero.
    const isZeroLike = (value) => {
        if (value === null || value === undefined) return false;
        const num = Number(typeof value === "string" ? value.trim() : value);
        return !Number.isNaN(num) && num === 0;
    };

    const transformB5SlippageData = (data) => {
        let transformed =
            data?.flatMap((group) => {
                const sharedProps = {
                    legId: group.legId,
                    groupId: group.groupId || group.legId,
                    isSampleVoyage: group.isSampleVoyage,
                    isVoyage: group.isVoyage,
                    portOfDeparture: group.portOfDeparture,
                    portOfArrival: group.portOfArrival,
                    startTime: group.startTime,
                    endTime: group.endTime,
                    activity: group.activity,
                    diffCriteria: group.diffCriteria,
                    totalLegEnergy: group.totalLegEnergy,
                    totalLegEnergyFEUScope: group.totalLegEnergyFEUScope,
                    throughIce: group.throughIce,
                    totalLegDist: group.totalLegDist,
                    totalLegDistThroughIce: group.totalLegDistThroughIce,
                    totalLegDistFEUScope: group.totalLegDistFEUScope,
                    totalLegDistThroughIceFEUScope: group.totalLegDistThroughIceFEUScope,
                };

                if (!group.fuels || group.fuels.length === 0) {
                    return {
                        ...sharedProps,
                        fuelCode: null,
                        fuelType: null,
                        lcvEu: null,
                        consumer: null,
                        cslip: null,
                        cons: null,
                        energy: null,
                        consThroughIce: null,
                        consThroughIceFEUScope: null,
                    };
                }

                return group.fuels.flatMap((fuel) => {
                    if (!fuel.consumers || fuel.consumers.length === 0) {
                        return {
                            ...sharedProps,
                            fuelCode: fuel.fuelCode,
                            fuelType: fuel.fuelType,
                            lcvEu: fuel.lcvEu,
                            consumer: null,
                            cslip: null,
                            cons: null,
                            energy: fuel.energy ?? null,
                            consThroughIce: fuel.consThroughIce ?? null,
                            consThroughIceFEUScope: fuel.consThroughIceFEUScope ?? null,
                        };
                    }

                    return fuel.consumers.map((consumer) => ({
                        ...sharedProps,
                        fuelCode: fuel.fuelCode,
                        fuelType: fuel.fuelType,
                        lcvEu: fuel.lcvEu,
                        consumer: consumer.consumer,
                        cslip: consumer.cslip,
                        cons: consumer.cons,
                        energy: consumer.energy,
                        consThroughIce: consumer.consThroughIce,
                        consThroughIceFEUScope: consumer.consThroughIceFEUScope,
                    }));
                });
            }) || [];

        // sort by legId and then fuelType
        transformed = transformed.sort((a, b) => {
            if (a.legId === b.legId) {
                return (a.fuelType || "").localeCompare(b.fuelType || "");
            }
            return (a.legId || 0) - (b.legId || 0);
        });

        // assign group flags (start, center)
        let grouped = [];
        let startIdx = 0;
        for (let i = 0; i < transformed.length; i++) {
            const current = transformed[i];
            const previous = transformed[i - 1];
            current.isGroupStart = i === 0 || current.legId !== previous?.legId;

            if (current.isGroupStart && i > 0) {
                const groupSize = i - startIdx;
                const centerIdx = startIdx + Math.floor(groupSize / 2);
                grouped[centerIdx].isGroupCenter = true;
                startIdx = i;
            }
            grouped.push(current);
        }
        const lastGroupSize = grouped.length - startIdx;
        const lastCenterIdx = startIdx + Math.floor(lastGroupSize / 2);
        grouped[lastCenterIdx].isGroupCenter = true;

        return grouped;
    };

    const fetchEnergyMonitoring = async (reportMstId, filter = "") => {
        setLoading(true);
        try {
            const response = await callApi({
                method: "GET",
                url: FEU_CHILD_API_ROUTES.GET_ENERGY_MONITORING(reportMstId, filter),
                data: {},
            });
            setLoading(false);
            if (!response?.fuelEuLegs || response.fuelEuLegs.length === 0) {
                setData([]);
                setTotalRow(null);
                return;
            }
            const totalRowData = response.fuelEuLegs.find(r => r.isTotal) || null;
            const transformed = transformB5SlippageData(
                response.fuelEuLegs.filter(r => !r.isTotal)
            );
            setData(transformed);
            setTotalRow(totalRowData);
        } catch (error) {
            setLoading(false);
            console.error("Error fetching energy monitoring data", error);
            setData([]);
            setTotalRow(null);
        }
    };


    useEffect(() => {
        if (reportDetails?.ReportMstId) {
            fetchEnergyMonitoring(reportDetails.ReportMstId);
        }
    }, [reportDetails?.ReportMstId]);

    const columns = useMemo(
        () => [
            {
                accessorKey: "legId",
                header: "Leg Id",
                id: "legId",
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <div className="d-flex justify-content-end font-weight-bold">
                            {row.original.isGroupCenter ? row.original.legId : ""}
                        </div>
                    )
                },
            },
            {
                id: "isSampleVoyage",
                accessorKey: "isSampleVoyage",
                header: "Is Sample",
                size: 80,
                Cell: ({ row }) => {
                    const isSampleVoyage = row?.original?.isSampleVoyage;
                    return (
                        <div className="d-flex justify-content-center">
                            {row.original.isGroupCenter
                                ? <Checkbox
                                    className="p-0"
                                    size="small"
                                    disabled={!isSampleVoyage}
                                    checked={isSampleVoyage}
                                />
                                : null}

                        </div>
                    );
                },
            },
            {
                id: "ports",
                accessorKey: "ports",
                header: "Port(s)",
                Cell: ({ row }) => {
                    return row.original.isGroupCenter
                        ? (row?.original?.isVoyage
                            ? `${row?.original?.portOfDeparture} --> ${row?.original?.portOfArrival}`
                            : `${row?.original?.portOfArrival} --> ${row?.original?.portOfDeparture}`)
                        : null
                },
            },
            {
                id: "startTime",
                accessorKey: "startTime",
                header: "Start Timestamp",
                Cell: ({ row }) => {
                    return (
                        <div className="d-flex justify-content-center">
                            {row.original.isGroupCenter ? convertLocaltoUTCFormat(row.original.startTime) : ""}
                        </div>
                    )
                }
            },
            {
                id: "endTime",
                accessorKey: "endTime",
                header: "End Timestamp",
                Cell: ({ row }) => {
                    return (
                        row.original.isGroupCenter ? convertLocaltoUTCFormat(row.original.endTime) : ""
                    )
                }
            },
            {
                id: "activity",
                accessorKey: "activity",
                header: "Activity",
                Cell: ({ row }) => {
                    return (
                        row.original.isGroupCenter ? row.original.activity : ""
                    )
                },
                size: 100,
            },
            {
                id: "diffCriteria",
                accessorKey: "diffCriteria",
                header: "Differentiating criteria",
                Cell: ({ row }) => {
                    return (
                        row.original.isGroupCenter ? row.original.diffCriteria : ""
                    )
                }
            },
            {
                id: "fuelCode",
                accessorKey: "fuelCode",
                header: "Fuel code",
                Cell: ({ row }) => row.original.fuelCode ?? "-",
            },
            {
                id: "fuelType",
                accessorKey: "fuelType",
                header: "Fuel type",
                Cell: ({ row }) => row.original.fuelType ?? "-",
            },
            {
                id: "lcvEu",
                accessorKey: "lcvEu",
                header: "LCV EU (MJ/g)",
                Cell: ({ row }) => {
                    const value = row.original.lcvEu;

                    return (
                        <div className="d-flex justify-content-end" title={value ?? "-"}>
                            {safeDisplay(row.original.lcvEu)}
                        </div>
                    );
                },
            },
            {
                id: "consumer",
                accessorKey: "consumer",
                header: "Consumer",
                Cell: ({ row }) => row.original.consumer ?? "-",
            },
            {
                id: "cslip",
                accessorKey: "cslip",
                header: "Cslip (%)",
                Cell: ({ row }) => {
                    return (
                        <div className="d-flex justify-content-end" title={row.original.cslip ?? ""}>
                            {isZeroLike(row.original.cslip) ? "" : (row.original.cslip !== null ? safeDisplay(row.original.cslip) : "-")}
                        </div>
                    )
                },
                Footer: () => <b className="footer-total-font d-flex justify-content-end">Total</b>,
            },
            {
                id: "cons",
                accessorKey: "cons",
                header: "CONS (MT)",
                Cell: ({ row }) => {
                    return (
                        <div className="d-flex justify-content-end" title={row.original.cons ?? ""}>
                            {isZeroLike(row.original.cons) ? "" : (row.original.cons !== null ? safeDisplay(row.original.cons) : "-")}
                        </div>
                    )
                },
                Footer: () =>
                    totalRow ? <b className="footer-total-font d-flex justify-content-end" title={totalRow.cons ?? ""}> {safeDisplay(totalRow.cons)}</b> : "",
            },
            {
                id: "energy",
                accessorKey: "energy",
                header: "Energy (GJ)",
                Cell: ({ row }) => {
                    return (
                        <div className="d-flex justify-content-end" title={row.original.energy ?? ""}>
                            {row.original.energy ? safeDisplay(row.original.energy) : "-"}
                        </div>
                    )
                },
                Footer: () =>
                    totalRow ? <b className="footer-total-font d-flex justify-content-end" title={totalRow.energy ?? ""}>{safeDisplay(totalRow.energy)}</b> : "",
            },
            {
                id: "totalLegEnergy",
                accessorKey: "totalLegEnergy",
                header: "Total leg energy (GJ)",
                Cell: ({ row }) => {
                    return (
                        <div className="d-flex justify-content-end" title={row.original.totalLegEnergy ?? ""}>
                            {row.original.isGroupCenter ? safeDisplay(row.original.totalLegEnergy) : ""}
                        </div>
                    )
                },
                Footer: () =>
                    totalRow ? <b className="footer-total-font d-flex justify-content-end" title={totalRow.totalLegEnergy ?? ""}>{safeDisplay(totalRow.totalLegEnergy)}</b> : "",
            },
            {
                id: "totalLegEnergyFEUScope",
                accessorKey: "totalLegEnergyFEUScope",
                header: "Total energy in FEU scope (GJ)",
                Cell: ({ row }) => {
                    return (
                        <div className="d-flex justify-content-end" title={row.original.totalLegEnergyFEUScope ?? ""}>
                            {row.original.isGroupCenter ? safeDisplay(row.original.totalLegEnergyFEUScope) : ""}
                        </div>
                    )
                },
                Footer: () =>
                    totalRow ? (
                        <b className="footer-total-font d-flex justify-content-end" title={totalRow.totalLegEnergyFEUScope ?? ""}>{safeDisplay(totalRow.totalLegEnergyFEUScope)}</b>
                    ) : "",
            },
            {
                id: "throughIce",
                accessorKey: "throughIce",
                header: "Through ice",
                size: 50,
                Cell: ({ row }) => {
                    const throughIce = row?.original?.throughIce;
                    return (
                        <div className="d-flex justify-content-center">
                            {row.original.isGroupCenter ?
                                <Checkbox
                                    className="p-0"
                                    size="small"
                                    disabled={!throughIce}
                                    checked={throughIce}
                                />
                                : null
                            }

                        </div>
                    );
                },
            },
            {
                id: "totalLegDist",
                accessorKey: "totalLegDist",
                header: "Total leg DIST (nm)",
                Cell: ({ row }) => {
                    return (
                        <div className="d-flex justify-content-end" title={row.original.totalLegDist ?? ""}>
                            {row.original.isGroupCenter ? safeDisplay(row.original.totalLegDist) : null}
                        </div>
                    )
                },
                Footer: () =>
                    totalRow ? (
                        <b className="footer-total-font d-flex justify-content-end" title={totalRow.totalLegDist ?? ""}> {safeDisplay(totalRow.totalLegDist)}</b>
                    ) : "",
            },
            {
                id: "totalLegDistThroughIce",
                accessorKey: "totalLegDistThroughIce",
                header: "Total leg DIST through ice (nm)",
                Cell: ({ row }) => {
                    return (
                        <div className="d-flex justify-content-end" title={row.original.totalLegDistThroughIce ?? ""}>
                            {row.original.isGroupCenter ? safeDisplay(row.original.totalLegDistThroughIce) : null}
                        </div>
                    )
                },
                Footer: () =>
                    totalRow ? (
                        <b className="footer-total-font d-flex justify-content-end" title={totalRow.totalLegDistThroughIce ?? ""}>{safeDisplay(totalRow.totalLegDistThroughIce)}</b>
                    ) : "",
            },
            {
                id: "consThroughIce",
                accessorKey: "consThroughIce",
                header: "CONS through ice (MT)",
                Cell: ({ row }) => {
                    return (
                        <div className="d-flex justify-content-end" title={row.original.consThroughIce ?? ""}>
                            {row.original.consThroughIce !== null ? safeDisplay(row.original.consThroughIce) : "-"}
                        </div>
                    )
                },
                Footer: () =>
                    totalRow ? (
                        <b className="footer-total-font d-flex justify-content-end" title={totalRow.consThroughIce ?? ""}>{safeDisplay(totalRow.consThroughIce)}</b>
                    ) : "",
            },
            {
                id: "totalLegDistFEUScope",
                accessorKey: "totalLegDistFEUScope",
                header: "Total leg DIST in FEU scope (nm)",
                Cell: ({ row }) => {
                    return (
                        <div className="d-flex justify-content-end" title={row.original.totalLegDistFEUScope ?? ""}>
                            {row.original.isGroupCenter ? safeDisplay(row.original.totalLegDistFEUScope) : ""}
                        </div>
                    )
                },
                Footer: () =>
                    totalRow ? (
                        <b className="footer-total-font d-flex justify-content-end" title={totalRow.totalLegDistFEUScope ?? ""}>{safeDisplay(totalRow.totalLegDistFEUScope)}</b>
                    ) : "",
            },
            {
                id: "totalLegDistThroughIceFEUScope",
                accessorKey: "totalLegDistThroughIceFEUScope",
                header: "Total leg DIST through ice in FEU scope (nm)",
                Cell: ({ row }) => {
                    return (
                        <div className="d-flex justify-content-end" title={row.original.totalLegDistThroughIceFEUScope ?? ""}>
                            {row.original.isGroupCenter ? safeDisplay(row.original.totalLegDistThroughIceFEUScope) : ""}
                        </div>
                    )
                },
                Footer: () =>
                    totalRow ? (
                        <b className="footer-total-font d-flex justify-content-end" title={totalRow.totalLegDistThroughIceFEUScope ?? ""}>{safeDisplay(totalRow.totalLegDistThroughIceFEUScope)}</b>
                    ) : "",
            },
            {
                id: "consThroughIceFEUScope",
                accessorKey: "consThroughIceFEUScope",
                header: "CONS through ice in FEU scope (MT)",
                Cell: ({ row }) => {
                    return (
                        <div className="d-flex justify-content-end" title={row.original.consThroughIceFEUScope ?? ""}>
                            {row.original.consThroughIceFEUScope !== null ? safeDisplay(row.original.consThroughIceFEUScope) : "-"}
                        </div>
                    )
                },
                Footer: () =>
                    totalRow ? (
                        <b className="footer-total-font d-flex justify-content-end" title={totalRow.consThroughIceFEUScope ?? ""}>{safeDisplay(totalRow.consThroughIceFEUScope)}</b>
                    ) : "",
            },
        ], [totalRow, data]
    );

    const table = useMaterialReactTable({
        columns: columns,
        data: data || [],
        enableStickyHeader: true,
        enableStickyFooter: true,
        enablePagination: false,
        enableSorting: false,
        enableTableFooter: data?.length > 0,
        enableRowSelection: false,
        enableTopToolbar: false,
        enableBottomToolbar: false,
        enableColumnActions: false,
        editDisplayMode: "row",
        state: {
            isLoading: loading,
        },
        muiTableHeadCellProps: {
            sx: {
                color: "white",
                backgroundColor: "#042a4c",
                borderRight: "1px solid lightgray",
                textAlign: "center",
                alignContent: "center"
            },
        },
        muiTablePaperProps: { sx: { boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)" } },
        displayColumnDefOptions: {
            "mrt-row-actions": {
                header: "Actions",
                size: 90,
                muiTableHeadCellProps: {
                    sx: { borderBottom: "none", borderRight: "1px solid lightgray" },
                },
                muiTableBodyCellProps: {
                    sx: {
                        borderRight: "1px solid lightgray",
                        padding: "4px",
                    },
                },
            },
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: "500px",
                overflowY: "auto",
            },
        },
        muiTableBodyCellProps: {
            sx: {
                borderRight: "1px solid lightgray",
                "&:last-child": { borderRight: "none" },
            },
        },
        muiTableBodyRowProps: ({ row, table }) => {
            const data = table.getRowModel().rows.map(r => r.original);

            const currentGroup = row.original?.groupId;
            const previousGroup = row.index > 0 ? data[row.index - 1]?.groupId : null;
            const isGroupStart = row.index === 0 || currentGroup !== previousGroup;
            const isGroupEnd = row.index === data?.length - 1;

            const groupedColumns = [1, 2, 3, 4, 5, 6, 7, 15, 16, 17, 18, 19, 21, 22];
            const selectors = groupedColumns.map(i => `& td:nth-of-type(${i})`).join(", ");
            return {
                sx: {
                    "& td": { fontSize: "0.875rem" },
                    [selectors]: {
                        borderTop: isGroupStart ? "1px solid #ccc" : "none",
                        borderBottom: isGroupEnd ? "1px solid #ccc" : "none",
                    },

                },
            };
        },
        muiTableFooterCellProps: {
            sx: {
                backgroundColor: "#f9f9f9",
                borderRight: "1px solid lightgray",
                "&:last-child": { borderRight: "none" },
            },
        },
    });

    return (
        <>
            <div className="mt-2">
                <MDBRow>
                    <MDBCol md="2">
                        <AbsDropDown
                            id="energy-monitoring-dropdown"
                            className="custom-dropdown align-items-center"
                            options={ENERGYMONITORINGFILTERDROPDOWN}
                            selected={selectedValue}
                            handleChange={(selected) => {
                                setSelectedValue(selected.value);
                                fetchEnergyMonitoring(reportDetails.ReportMstId, selected.value)
                            }}
                            placeholder="Filter"
                        />
                    </MDBCol>
                </MDBRow>
            </div>
            <div className="feu-energy-table mt-3">
                <MaterialReactTable table={table} />
            </div>
        </>
    );
};

export default FEUMRVEnergyMonitoring;